import { clientEnv } from '@/shared/config/env';
import rosaMxBundledThemeTokens from '../../../public/skins/rosa-mx-h5/theme-tokens.json';
import type { SkinManifest } from '@/shared/theme/orange-sports-skin';
import type { RouteRecordRaw } from 'vue-router';
import type { SkinModule } from '@/shared/theme/skin-module';
import { rosaMxArticleList } from './article-data';
import { ROSA_MX_SKIN_CODE, rosaMxAssets, rosaMxSkinRoot, rosaMxThemeTokenUrl } from './assets';
import { bindRosaMxRem } from './rem';

/**
 * Rosa MX H5（siteId rosacasino-mx-h5）—— 源站 tau777.mx（镜像 h5i6.com）1:1 复刻。
 * 这是唯一挂进 B-lite 注册表的皮肤模块；本文件必须保持纯数据 + 懒加载，禁止静态引 .vue / messages.ts。
 */
export const rosaMxH5Skin: SkinManifest = {
  assets: {
    avatar: `${rosaMxSkinRoot}/brand/logo.png`,
    chests: {
      claimable: `${rosaMxSkinRoot}/quick/canjear.webp`,
      locked: `${rosaMxSkinRoot}/quick/misterioso.webp`,
      opened: `${rosaMxSkinRoot}/quick/semana.webp`,
    },
    hero: rosaMxAssets.banners[0],
    logo: rosaMxAssets.header.logo,
    pixQr: '',
  },
  brandName: 'Rosa Casino',
  campaigns: [],
  categories: [],
  code: ROSA_MX_SKIN_CODE,
  games: [],
};

/**
 * 源站游客态（CDP 实测）：Personal(#/profile) 可打开并显示游客卡；Depositar / Retirar / 邀请 / 活动详情点击即弹注册抽屉，
 * Bonos 弹登录抽屉。记录页源站游客不可达，这里保留旧口径不额外放开。
 */
const ROSA_MX_GUEST_BROWSABLE_ROUTES = new Set<string>(['account']);

/**
 * 源站静态抽屉页 → 独立路由（游客可浏览）：
 *   #/service → /service；#/help #/terms #/preventLaunderMoney #/privacy #/exclusionPolicy #/responsible #/underagePolicy → 7 条条款页。
 * name 统一加 rosa 前缀避免与共享路由撞名。
 */
const loadArticlePage = () => import('./pages/RosaMxArticlePage.vue');
const ROSA_MX_ROUTES: RouteRecordRaw[] = [
  {
    component: () => import('./pages/RosaMxServicePage.vue'),
    name: 'rosaService',
    path: '/service',
  },
  {
    /** 源站 #/recharge/rechargeRecord */
    component: () => import('./pages/RosaMxDepositRecordPage.vue'),
    name: 'rosaDepositRecords',
    path: '/deposit/records',
  },
  {
    /** 源站 #/withdrawal/withdrawalHistory */
    component: () => import('./pages/RosaMxRecordsPage.vue'),
    name: 'rosaWithdrawHistory',
    path: '/withdraw/history',
  },
  {
    component: () => import('./pages/RosaMxEngineActivityPage.vue'),
    meta: { rosaEngine: 'misterioso' },
    name: 'rosaMisterioso',
    path: '/misterioso',
  },
  {
    component: () => import('./pages/RosaMxWeeklySalaryPage.vue'),
    name: 'rosaSemana',
    path: '/semana',
  },
  {
    /** 源站 #/gameSearch */
    component: () => import('./pages/RosaMxGameSearchPage.vue'),
    name: 'rosaGameSearch',
    path: '/search',
  },
  {
    component: () => import('./pages/RosaMxEngineActivityPage.vue'),
    meta: { rosaEngine: 'promocional' },
    name: 'rosaPromocional',
    path: '/promocional',
  },
  {
    component: () => import('./pages/RosaMxEngineActivityPage.vue'),
    name: 'rosaActivityDetail',
    path: '/act/:activityId',
  },
  {
    component: () => import('./pages/RosaMxRankingPage.vue'),
    name: 'rosaRanking',
    path: '/ranking',
  },
  ...rosaMxArticleList.map<RouteRecordRaw>((article) => ({
    component: loadArticlePage,
    meta: { rosaArticle: article.key },
    name: `rosaArticle:${article.key}`,
    path: `/${article.path}`,
  })),
];

export const rosaMxSkinModule: SkinModule = {
  bindRem: bindRosaMxRem,
  bundledThemeTokens: rosaMxBundledThemeTokens,
  code: ROSA_MX_SKIN_CODE,
  guestBrowsableRoutes: ROSA_MX_GUEST_BROWSABLE_ROUTES,
  locale: {
    fallback: 'es-MX',
    supported: [
      { code: 'es-MX', flag: '🇲🇽', label: 'Español' },
      { code: 'en-US', flag: '🇺🇸', label: 'English' },
      ...(clientEnv.appEnv === 'test' ? [{ code: 'zh-CN' as const, flag: '🇨🇳', label: '简体中文' }] : []),
    ],
  },
  manifest: rosaMxH5Skin,
  mockTenant: {
    brandId: 'rosacasino',
    countryCode: 'MX',
    currency: 'MXN',
    defaultLanguage: 'es-MX',
    layoutTemplateId: 'rosa-mx-h5-home',
    licenseTextKey: 'compliance.license.mx',
    minAge: 18,
    paymentType: 'card',
    pixDeposit: false,
    runtimeVersion: 'r-rosa-mx-local',
    supportedLanguages: ['es-MX', 'en-US'],
    themeTokenUrl: rosaMxThemeTokenUrl,
    themeVersion: 'rosa-mx-h5',
  },
  authModalClass: 'auth-modal--rosa-mx',
  authModalTeleport: '.app-frame',
  authPanel: () => import('./auth/RosaMxAuthPanel.vue'),
  pages: {
    account: () => import('./pages/RosaMxAccountPage.vue'),
    agentShare: () => import('./pages/RosaMxAgentPage.vue'),
    bonus: () => import('./pages/RosaMxBonusPage.vue'),
    deposit: () => import('./pages/RosaMxDepositPage.vue'),
    gameDetails: () => import('./pages/RosaMxGameDetailsPage.vue'),
    games: () => import('./pages/RosaMxCasinoPage.vue'),
    home: () => import('./pages/RosaMxHomePage.vue'),
    lotteryWheel: () => import('./pages/RosaMxLotteryWheelPage.vue'),
    notice: () => import('./pages/RosaMxNoticePage.vue'),
    promotions: () => import('./pages/RosaMxPromotionsPage.vue'),
    records: () => import('./pages/RosaMxRecordsPage.vue'),
    redeem: () => import('./pages/RosaMxRedeemPage.vue'),
    signIn: () => import('./pages/RosaMxSignInPage.vue'),
    vip: () => import('./pages/RosaMxVipPage.vue'),
    withdraw: () => import('./pages/RosaMxWithdrawPage.vue'),
  },
  rootClass: 'skin-rosa-mx',
  routes: ROSA_MX_ROUTES,
  shell: () => import('./shell/RosaMxShell.vue'),
  themeTokenUrl: rosaMxThemeTokenUrl,
};
